import { Checkmark32, Close32, View32, ViewOff32 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, forwardRef, useState } from 'react';

export interface PasswordRequirementType {
  [key: string]: {
    isValid: boolean;
    message: string;
  };
}

export interface PasswordInputProps extends React.HTMLProps<HTMLInputElement> {
  placeholder?: string;
  highlightInput?: boolean;
  customStyle?: string;
  disabled?: boolean;
  requirements?: PasswordRequirementType;
  errorMessage?: string;
  label?: string;
  inputName?: string;
}

// eslint-disable-next-line react/display-name
const PasswordInput = forwardRef<FC, React.PropsWithoutRef<PasswordInputProps>>(
  (props, ref): JSX.Element => {
    const {
      placeholder,
      highlightInput,
      customStyle,
      disabled,
      requirements,
      errorMessage,
      label,
      inputName = `input--${(label || placeholder)?.replace(' ', '')}`,
      ...restProps
    } = props;
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [value, setValue] = useState<string>('');
    const showLabel = !value && label;

    const className = {
      inputContainer: clsx(
        'h-[50px] min-w-0 px-3px py-[2px] relative ring-1 ring-inset rounded-default',
        'ring-purple80 w-full monochrome:ring-grey90 focus-within:ring-[3px] hover:ring-[3px]',
        highlightInput && 'ring-[3px]',
        disabled && '!ring-grey50 focus-within:ring-1 hover:ring-1',
        errorMessage && 'ring-red60 !ring-[3px]'
      ),
      inputField: clsx(
        '!z-20 base-input-anim bg-transparent border-0 font-weight-bold max-w-full min-h-full min-w-0 relative',
        'outline-none pl-20px placeholder:font-weight-normal pr-45px w-full',
        'placeholder-purple80 text-purple80 focus:placeholder-lightPurple',
        'monochrome:focus:placeholder-grey60 monochrome:placeholder-black monochrome:text-black',
        disabled &&
          'placeholder-grey50 monochrome:placeholder-grey50 text-grey50 monochrome:text-grey50 select-none',
        errorMessage &&
          '!text-red60 !focus:placeholder-red60 !placeholder-red60'
      ),
      inputError: clsx(
        'absolute block left-0 mt-5px text-p3 text-red60 w-full'
      ),
      buttonStyle: clsx(
        '!z-20 absolute bottom-15px right-20px',
        !disabled && 'text-purple80 monochrome:text-black',
        disabled && 'text-grey50 monochrome:text-grey50',
        errorMessage && '!text-red60'
      ),
      validationIcon: 'flex h-auto w-10px text-grey60',
      validation:
        'border-grey20 aurego-validation rounded-default border-1px px-[12.5px] py-[10px] mt-10px',
      validationContainer: 'flex items-center mb-5px  last:mb-0',
      validationText: 'text-grey60 text-[10px] ml-[12.5px] w-full',
      validText: 'text-functional03',
      labelStyle: clsx(
        '!z-10 absolute bottom-[15px] duration-300 leading-5 left-20px px-3px text-p1',
        'bg-white text-purple80',
        'monochrome:text-black',
        errorMessage && '!text-red60'
      ),
    };

    const {
      inputContainer,
      inputField,
      inputError,
      buttonStyle,
      validationIcon,
      validation,
      validationContainer,
      validationText,
      validText,
      labelStyle,
    } = className;

    return (
      <div className={customStyle}>
        <div className={inputContainer}>
          <input
            name={inputName}
            aria-label={label}
            type={showPassword ? 'text' : 'password'}
            className={inputField}
            placeholder={placeholder}
            disabled={disabled}
            ref={ref as unknown as React.MutableRefObject<HTMLInputElement>}
            onChange={(e) => setValue(e.target.value)}
            required
            {...restProps}
          />
          {showLabel && (
            <label htmlFor={inputName} className={labelStyle}>
              {label}
            </label>
          )}
          <button
            type="button"
            className={buttonStyle}
            disabled={disabled}
            onClick={() => {
              setShowPassword((prevToggle) => !prevToggle);
            }}
          >
            {!showPassword && <View32 className="h-auto w-20px" />}
            {showPassword && <ViewOff32 className="h-auto w-20px" />}
          </button>
          {errorMessage && (
            <>
              <span className={inputError}>{errorMessage}</span>
            </>
          )}
        </div>

        {requirements && (
          <div className={validation}>
            {Object.keys(requirements).map((key: string, index: number) => {
              const isValid =
                requirements[key as keyof PasswordRequirementType].isValid;

              return (
                <div className={validationContainer} key={index}>
                  {isValid && (
                    <Checkmark32 className={`${validationIcon} ${validText}`} />
                  )}
                  {!isValid && <Close32 className={validationIcon} />}
                  <span
                    className={`${validationText} ${isValid ? validText : ''}`}
                  >
                    {requirements[key as keyof PasswordRequirementType].message}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

export default PasswordInput;
